/**
 * The own profile (web-plan.md F2): name/bio through `PATCH /v1/me/profile`
 * (`""` clears, absent leaves alone — D65; every answer is the whole
 * UserJson, no delta merging — D64), the avatar through the upload protocol
 * plus `PUT /v1/me/avatar` behind a client-side ≤5 MiB and magic-bytes
 * pre-check (D61/D63 — the server checks only at attach, after the bytes and
 * the quota are spent), and the password change with the R44 socket-swap
 * choreography.
 */
import { type JSX, Show, createSignal } from "solid-js";

import { currentLifetime, isCurrent } from "../lib/lifetime";
import { AvatarCircle } from "../components/avatar";
import { Button, Icon } from "../components/ds";
import type { Dict } from "../lib/i18n";
import type { Runtime } from "../store/runtime";
import * as mut from "../store/mutators";

const AVATAR_MAX_BYTES = 5 * 1024 * 1024;

export function ProfileScreen(props: {
  rt: Runtime;
  t: Dict;
  onClose(): void;
}): JSX.Element {
  const rt = props.rt;
  const s = rt.state;
  const [displayName, setDisplayName] = createSignal(s.account.displayName ?? "");
  const [bio, setBio] = createSignal(s.account.bio ?? "");
  const [currentPw, setCurrentPw] = createSignal("");
  const [newPw, setNewPw] = createSignal("");
  const [saving, setSaving] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);
  let avatarPick!: HTMLInputElement;

  const save = async () => {
    const lifetime = currentLifetime();
    setSaving(true);
    setError(null);
    try {
      await rt.saveProfile(displayName(), bio());
      if (!isCurrent(lifetime)) return;
      props.onClose();
    } catch (e) {
      if (!isCurrent(lifetime)) return;
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      if (isCurrent(lifetime)) {
        setSaving(false);
      }
    }
  };

  const changePassword = async () => {
    const lifetime = currentLifetime();
    if (currentPw() === "" || newPw() === "") return;
    setSaving(true);
    setError(null);
    try {
      await rt.changePassword(currentPw(), newPw());
      if (!isCurrent(lifetime)) return;
      setCurrentPw("");
      setNewPw("");
    } catch (e) {
      if (!isCurrent(lifetime)) return;
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      if (isCurrent(lifetime)) {
        setSaving(false);
      }
    }
  };

  const pickAvatar = async (file: File) => {
    const lifetime = currentLifetime();
    setError(null);
    // The pre-check (D61/D63): size and the leading bytes, BEFORE the
    // transfer — the server refuses only at attach, after the quota is spent.
    if (file.size > AVATAR_MAX_BYTES) {
      setError(`an avatar may be at most ${AVATAR_MAX_BYTES / (1024 * 1024)} MiB`);
      return;
    }
    const head = new Uint8Array(await file.slice(0, 16).arrayBuffer());
    if (!isCurrent(lifetime)) return;
    if (!looksLikeImage(head)) {
      setError("that file is not a webp, jpeg, png or avif");
      return;
    }
    setSaving(true);
    try {
      await rt.uploadAvatar(file);
      if (!isCurrent(lifetime)) return;
    } catch (e) {
      if (!isCurrent(lifetime)) return;
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      if (isCurrent(lifetime)) {
        setSaving(false);
      }
    }
  };

  const selfName = () => mut.nameOf(s.directory, mut.selfIdOf(s.directory));

  // No .echo-transition-enter on this scrim: the card centers by its own inline
  // transform, and the fallback rise keyframe would stomp it. The view
  // transition (swap "sheet") animates the snapshot instead.
  return (
    <div class="echo-scrim" onClick={() => props.onClose()}>
      <div style="display:contents" onClick={(e) => e.stopPropagation()}>
        <div
          class="profile-card profile-card--profile echo-material-thick"
          style="position:absolute;left:50%;top:50%;transform:translate(-50%,-50%);width:340px;padding:20px;border-radius:20px;display:flex;flex-direction:column;gap:12px"
        >
          <div style="display:flex;align-items:center;gap:12px">
            <button
              type="button"
              style="background:none;border:none;padding:0;cursor:pointer"
              onClick={() => avatarPick.click()}
              title={props.t.profileChangePhoto()}
            >
              <AvatarCircle
                name={selfName()}
                createdAt={s.account.createdAt}
                size={46}
                fileId={s.account.avatarFileId}
              />
            </button>
            <div style="display:flex;flex-direction:column;min-width:0">
              <span style="font-weight:600">{s.selfUsername}</span>
              <Show when={s.account.email}>
                <span style="font-size:12px;color:var(--labels-secondary)">{s.account.email}</span>
              </Show>
            </div>
            <span class="layout-spacer" />
            <Show when={s.account.avatarFileId}>
              <button
                type="button"
                class="echo-button echo-button--small echo-button--borderless"
                onClick={() => void rt.removeAvatar()}
              >
                <Icon name="delete" size={15} />
              </button>
            </Show>
          </div>

          <input
            class="composer__input"
            style="min-height:38px"
            placeholder={props.t.profileDisplayName()}
            value={displayName()}
            onInput={(e) => setDisplayName(e.currentTarget.value)}
          />
          <textarea
            class="composer__input"
            rows="3"
            placeholder={props.t.profileBio()}
            value={bio()}
            onInput={(e) => setBio(e.currentTarget.value)}
          />
          <Button icon="check" kind="prominent" size="medium" full disabled={saving()} onClick={() => void save()}>
            {props.t.consoleDone()}
          </Button>

          <div class="echo-menu__separator" />

          <input
            class="composer__input"
            style="min-height:38px"
            type="password"
            placeholder={props.t.loginPassword()}
            value={currentPw()}
            onInput={(e) => setCurrentPw(e.currentTarget.value)}
          />
          <input
            class="composer__input"
            style="min-height:38px"
            type="password"
            placeholder={`${props.t.loginPassword()} +`}
            value={newPw()}
            onInput={(e) => setNewPw(e.currentTarget.value)}
          />
          <Button
            size="medium"
            kind="bordered"
            icon="edit"
            full
            disabled={saving() || currentPw() === "" || newPw() === ""}
            onClick={() => void changePassword()}
          >
            {props.t.profileChangePassword()}
          </Button>

          <Show when={error()}>
            <div class="login__error">{error()}</div>
          </Show>

          <input
            ref={avatarPick}
            type="file"
            accept="image/webp,image/jpeg,image/png,image/avif"
            style="display:none"
            onChange={(e) => {
              const f = e.currentTarget.files?.[0];
              if (f) void pickAvatar(f);
              e.currentTarget.value = "";
            }}
          />
        </div>
      </div>
    </div>
  );
}

/** webp / jpeg / png / avif by their leading bytes — the same check the
 * desktop's profile::check_avatar does before a byte moves. AVIF is still on
 * the list although nothing writes one any more: it was our own format until
 * the photo path moved to WebP, so a picture saved out of an older channel has
 * to be pickable again. */
function looksLikeImage(head: Uint8Array): boolean {
  if (head.length >= 3 && head[0] === 0xff && head[1] === 0xd8 && head[2] === 0xff) return true; // jpeg
  if (
    head.length >= 8 &&
    head[0] === 0x89 &&
    head[1] === 0x50 &&
    head[2] === 0x4e &&
    head[3] === 0x47
  )
    return true; // png
  if (head.length >= 12) {
    if (
      String.fromCharCode(...head.slice(0, 4)) === "RIFF" &&
      String.fromCharCode(...head.slice(8, 12)) === "WEBP"
    )
      return true; // webp
    const brand = String.fromCharCode(...head.slice(4, 12));
    if (brand.startsWith("ftypavif") || brand.startsWith("ftypavis")) return true; // avif
  }
  return false;
}
